import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { get } from 'lodash';

import { Title } from 'projects/workspace/src/app/administration/models/company-profile.model';
import { PartnersTypeEnum } from 'projects/workspace/src/app/partners/corporate/enums/partner-types.enum';
import { PartnerContactModel } from 'projects/workspace/src/app/partners/corporate/models/partner-contact.model';
import { PartnerModel } from 'projects/workspace/src/app/partners/corporate/models/partner.model';
import { PartnersApiService } from 'projects/workspace/src/app/partners/corporate/services/partner-api.service';
import { CommonModalsActionsEnum, DangerModalComponent } from '../../modals-common';
import { BranchOfficeModel } from 'projects/workspace/src/app/partners/corporate/models';

@Component({
  selector: 'rnpl-create-new-contact-modal',
  templateUrl: './create-new-contact-modal.component.html',
  styleUrls: ['./create-new-contact-modal.component.scss'],
})
export class CreateNewContactModalComponent implements OnInit {
  public form: FormGroup;

  public titleItems: Title[] = [];

  readonly createContactRequest$: BehaviorSubject<boolean> = new BehaviorSubject(null);
  readonly branchOfficeList$: BehaviorSubject<BranchOfficeModel[]> = new BehaviorSubject([]);
  readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  public positionItems = [
    {value: '-', label: '-'},
    {value: 'CEO', label: 'USER_POSITIONS.CEO'},
    {value: 'C-level manager', label: 'USER_POSITIONS.C_LEVEL'},
    {value: 'Head of Department', label: 'USER_POSITIONS.HEAD_OF_DEPARTMENT'},
    // {value: 'Team Lead', label: 'Team Lead'},
    {value: 'Employee', label: 'USER_POSITIONS.EMPLOYEE'},
    {value: 'Intern', label: 'USER_POSITIONS.INTERN'},
    {value: 'Contractor', label: 'USER_POSITIONS.CONTRACTOR'},
  ];

  public departmentItems = [
    {value: '-', label: '-'},
    {value: 'Accounting', label: 'CATEGORIES.ACCOUNTING'},
    {value: 'Warehouse', label: 'MODULE.WAREHOUSE'},
    {value: 'Wholesale', label: 'DEPARTMENTS.TRADE'},
    // {value: 'Retail', label: 'DEPARTMENTS.RETAIL'},
    // {value: 'Logistics', label: 'DEPARTMENTS.LOGISTICS'},
    {value: 'Management', label: 'APP.MANAGEMENT'},
    {value: 'Legal', label: 'DEPARTMENTS.LEGAL'},
    {value: 'IT', label: 'DEPARTMENTS.IT'},
    {value: 'Marketing', label: 'DEPARTMENTS.MARKETING_PR'},
    // {value: 'Customer care', label: 'DEPARTMENTS.CUSTOMER'},
    // {value: 'HR', label: 'DEPARTMENTS.HR'},
    // {value: 'Services', label: 'CATEGORIES.SERVICES'},
    {value: 'E-commerce', label: 'MODULE.E_COMMERCE'},
  ];

  constructor(
    private readonly partnerApiService: PartnersApiService,
    private readonly fb: FormBuilder,
    public dialogRef: MatDialogRef<CreateNewContactModalComponent>,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: { partner?: PartnerModel; contact?: PartnerContactModel }
  ) {
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });
  }

  ngOnInit(): void {
    this.initForm();
    this.getBranchOffices();

    this.partnerApiService.getTitles()
      .pipe(takeUntil(this.destroy$))
      .subscribe((titles: Title[]) => {
        this.titleItems = titles;
      });
  }

  private initForm(): void {
    this.form = this.fb.group({
      firstName: [get(this.contact, 'firstName', ''), [Validators.required]],
      lastName: [get(this.contact, 'lastName', ''), [Validators.required]],
      phone: [get(this.contact, 'phone', ''), []],
      email: [get(this.contact, 'email', ''), [Validators.email]],
      title: [get(this.contact, 'title', null), []],
      // birthday: [get(this.contact, 'birthday', ''), []],
      // gender: [get(this.contact, 'gender', 'Unspecified'), [Validators.required]],
      department: [get(this.contact, 'department', null)],
      position: [get(this.contact, 'position', null)],
      branchId: [get(this.contact, 'branchId', null)],
    });

    if (
      get(this.contact, 'isCorporateContact', false) ||
      get(this.contact, 'contactType') === 'accounting'
    ) {
      this.form.disable();
    }

    if (this.contact.id) {
      this.form.get('branchId').disable();
    }
  }

  public getBranchOffices(): void {
    this.partnerApiService.getPartnerBranchOffices(this.partner.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data: BranchOfficeModel[]) => {
          const branches: BranchOfficeModel[] = data && data.length
            ? [...data, {name: '-', value: null} as any]
            : [];
          this.branchOfficeList$.next(branches);
        });
  }

  public createContact() {
    if (this.createContactRequest$.getValue()) { return; }
    this.createContactRequest$.next(true);
    this.partnerApiService
      .createPartnerContact(this.partner.id, PartnersTypeEnum.CORPORATE, this.form.getRawValue())
      .pipe(
        finalize(() => { this.createContactRequest$.next(false); }),
        takeUntil(this.destroy$),
      )
      .subscribe((contact: PartnerContactModel) => {
        this.close(contact);
      });
  }

  public saveContact() {
    this.partnerApiService
      .updatePartnerContact(this.partner.id, this.contact.id, PartnersTypeEnum.CORPORATE, this.form.getRawValue())
      .subscribe((_) => {
        this.close(true);
      });
  }

  public openRemoveContactModal() {
    const dialog = this.dialog.open(DangerModalComponent, {
      data: {
        message: `PARTNERS.YOU_ARE_ABOUT_TO_DELETE_CONTACT`,
      },
    });

    dialog.afterClosed().subscribe((response: CommonModalsActionsEnum) => {
      if (response === CommonModalsActionsEnum.CONFIRM) {
        this.partnerApiService.deletePartnerContact(this.partner.id, this.contact.id, PartnersTypeEnum.CORPORATE).subscribe((_) => {
          this.close(true);
        });
      }
    });
  }

  public close(success: boolean|PartnerContactModel = false): void {
    this.dialogRef.close(success);
  }

  public get contact(): PartnerContactModel {
    return get(this.data, 'contact', {} as PartnerContactModel);
  }

  public get partner(): PartnerModel {
    return get(this.data, 'partner', {} as PartnerModel);
  }

  public get position(): FormControl {
    return this.form.get('position') as FormControl;
  }

  public get department(): FormControl {
    return this.form.get('department') as FormControl;
  }

  // public get gender(): FormControl {
  //   return this.form.get('gender') as FormControl;
  // }

  // public get birthday(): FormControl {
  //   return this.form.get('birthday') as FormControl;
  // }
}
